import {useEffect, useState} from "react";
import {App as AntdApp, Alert, Collapse, Drawer, Input, Select, Steps, Tag, Upload} from "antd";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import Markdown from "~/features/AiTool/components/Markdown";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetContentItemQuery,
	useGetContentTargetsQuery,
	useApproveContentItemMutation,
	useRetryContentItemMutation,
	useUpdateContentItemMutation,
	useReplaceContentImageMutation,
	usePublishContentItemMutation,
} from "~/reduxs/api/contentApiSlice";
import {PIPELINE_STEPS, statusMeta} from "./statusMeta";

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Dàn ý (chờ duyệt / tham khảo): title + góc nhìn + sections + vị trí ảnh. */
function OutlineView({item}) {
	const outline = item.outline || {};

	return (
		<div>
			<h4 style={{margin: '0 0 4px'}}>{outline.title}</h4>
			{item.angle && <p style={{color: '#6b7280', fontSize: 13}}>Góc nhìn: {item.angle}</p>}

			<ol style={{paddingLeft: 20}}>
				{(outline.sections || []).map((s, i) => (
					<li key={i} style={{marginBottom: 8}}>
						<b>{s.heading}</b>
						{Array.isArray(s.points) && s.points.length > 0 && (
							<div style={{fontSize: 13, color: '#4b5563'}}>{s.points.join(' · ')}</div>
						)}
						{Array.isArray(s.questions) && s.questions.length > 0 && (
							<div style={{fontSize: 12.5, color: '#0891b2'}}>Trả lời: {s.questions.join(' / ')}</div>
						)}
						{Array.isArray(s.keywords) && s.keywords.length > 0 && (
							<div style={{fontSize: 12.5, color: '#6b7280'}}>Từ khoá: {s.keywords.join(', ')}</div>
						)}
					</li>
				))}
			</ol>

			{(outline.image_slots || []).length > 0 && (
				<p style={{fontSize: 12.5, color: '#6b7280'}}>
					<FontAwesomeIcon icon="fa-light fa-image" />{' '}
					{outline.image_slots.length} ảnh minh hoạ sẽ chèn sau phần: {outline.image_slots.map((s) => s.after_section).join(', ')}
				</p>
			)}
		</div>
	);
}

/** Checklist SEO: từng tiêu chí đạt/chưa. */
function ChecklistView({checklist}) {
	if (!checklist?.items) return null;

	return (
		<ul style={{listStyle: 'none', padding: 0, margin: 0}}>
			{checklist.items.map((c) => (
				<li key={c.key} style={{padding: '4px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13}}>
					<FontAwesomeIcon
						icon={c.pass ? 'fa-light fa-circle-check' : 'fa-light fa-circle-xmark'}
						style={{color: c.pass ? '#16a34a' : '#dc2626', marginRight: 6}}
					/>
					{c.label}
					{!c.pass && c.note && <span style={{color: '#6b7280'}}> — {c.note}</span>}
				</li>
			))}
		</ul>
	);
}

/**
 * Drawer xem chi tiết 1 bài viết AI: thanh tiến trình pipeline + nội dung theo trạng thái
 * (duyệt dàn ý / xem bài + checklist + sửa markdown / lỗi + chạy lại). Poll 5s khi đang chạy.
 */
function ContentViewer({projectId, itemId, onClose}) {

	const {message} = AntdApp.useApp();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');

	// Poll 5s CHỈ khi bài đang chạy nền.
	const [pollMs, setPollMs] = useState(0);
	const {data: item, isLoading} = useGetContentItemQuery(
		{projectId, itemId},
		{skip: !itemId, pollingInterval: pollMs, skipPollingIfUnfocused: true}
	);

	useEffect(() => {
		setPollMs(item?.running ? 5000 : 0);
	}, [item]);

	const [approve, {isLoading: approving}] = useApproveContentItemMutation();
	const [retry, {isLoading: retrying}] = useRetryContentItemMutation();
	const [update, {isLoading: saving}] = useUpdateContentItemMutation();
	const [replaceImage, {isLoading: uploading}] = useReplaceContentImageMutation();
	const [publish, {isLoading: publishing}] = usePublishContentItemMutation();

	// Đợt 3: chọn kênh + chế độ khi đăng bài (chỉ tải khi drawer mở).
	const {data: targetData} = useGetContentTargetsQuery(projectId, {skip: !itemId});
	const [publishTarget, setPublishTarget] = useState(null);
	const [publishMode, setPublishMode] = useState('');

	const activeTargets = (targetData?.targets || []).filter((t) => t.status === 'active');

	const doPublish = async () => {
		if (!publishTarget) {
			message.warning('Chọn kênh đăng trước.');
			return;
		}
		try {
			await publish({projectId, itemId, target_id: publishTarget, status: publishMode || undefined}).unwrap();
			message.success('Đã xếp hàng đăng bài — hệ thống đăng trong nền');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	// Đợt 2: upload thay ảnh 1 slot — đọc file thành data URL rồi POST JSON (≤4MB).
	const uploadImage = (file, role, index) => {
		if (file.size > 4 * 1024 * 1024) {
			message.error('Ảnh vượt 4MB — chọn ảnh nhỏ hơn.');
			return false;
		}
		const reader = new FileReader();
		reader.onload = async () => {
			try {
				await replaceImage({projectId, itemId, role, index, data: reader.result}).unwrap();
				message.success('Đã thay ảnh — checklist đã chấm lại');
			} catch (e) {
				message.error(rtkErrorMessage(e));
			}
		};
		reader.readAsDataURL(file);
		return false; // chặn antd tự upload
	};

	// Đóng/mở bài khác → thoát chế độ sửa.
	useEffect(() => {
		setEditing(false);
	}, [itemId]);

	const act = async (fn, okMsg) => {
		try {
			await fn().unwrap();
			if (okMsg) message.success(okMsg);
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const saveDraft = async () => {
		try {
			await update({projectId, itemId, content_md: draft}).unwrap();
			setEditing(false);
			message.success('Đã lưu — checklist đã chấm lại');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const meta = item ? statusMeta(item.status) : null;
	const featured = (item?.images || []).find((im) => im.role === 'featured');

	return (
		<Drawer
			open={Boolean(itemId)}
			onClose={onClose}
			width={Math.min(900, window.innerWidth - 24)}
			title={item ? (item.title || item.keyword) : 'Bài viết AI'}
			extra={meta && <Tag color={meta.color}>{meta.label}</Tag>}
		>
			{(isLoading || !item) && <Loading />}

			{item && (
				<div>
					{meta.step >= 0 && (
						<Steps
							size="small" current={meta.step}
							status={item.status === 'done' ? 'finish' : 'process'}
							items={PIPELINE_STEPS.map((t) => ({title: t}))}
							style={{marginBottom: 16}}
						/>
					)}

					{item.error && item.status !== 'failed' && (
						<Alert type="warning" showIcon style={{marginBottom: 12}} message="Pipeline đang chờ" description={item.error} />
					)}

					{item.status === 'failed' && (
						<Alert
							type="error" showIcon style={{marginBottom: 12}}
							message="Bài viết gặp lỗi" description={item.error}
							action={
								<Button small primary loading={retrying} onClick={() => act(() => retry({projectId, itemId}), 'Đã đưa bài trở lại hàng đợi')}>
									Chạy lại
								</Button>
							}
						/>
					)}

					{item.running && (
						<Alert
							type="info" showIcon style={{marginBottom: 12}}
							message="AI đang xử lý trong nền — trang tự cập nhật mỗi 5 giây"
							description={item.status === 'write' && item.sections_total > 0
								? `Đã viết ${item.write_cursor}/${item.sections_total} phần.`
								: item.intent_source === 'serp' || item.status === 'intent'
									? 'Đang lấy dữ liệu top 10 Google + People Also Ask + AI Overview cho từ khoá.'
									: undefined}
						/>
					)}

					{/* ---- Chờ duyệt dàn ý ---- */}
					{item.status === 'outline_review' && (
						<>
							<Alert
								type="warning" showIcon style={{marginBottom: 12}}
								message="Dàn ý chờ bạn duyệt"
								description="Duyệt để AI viết bài theo dàn ý này. (Chế độ duyệt chỉnh ở tab Thương hiệu & Cài đặt.)"
								action={
									<Button small primary loading={approving} onClick={() => act(() => approve({projectId, itemId}), 'Đã duyệt — AI đang viết bài')}>
										Duyệt dàn ý
									</Button>
								}
							/>
							<OutlineView item={item} />
						</>
					)}

					{/* ---- Bài đã viết xong (chờ duyệt / hoàn tất / đã đăng) ---- */}
					{['review', 'done', 'published'].includes(item.status) && (
						<>
							{item.status === 'review' && (
								<Alert
									type="warning" showIcon style={{marginBottom: 12}}
									message={`Bài viết chờ duyệt — điểm checklist SEO ${item.checklist_score}/100`}
									action={
										<Button small primary loading={approving} onClick={() => act(() => approve({projectId, itemId}), 'Đã duyệt bài viết')}>
											Duyệt bài
										</Button>
									}
								/>
							)}

							{/* Đợt 3: đã đăng → link bài trên kênh + hiệu quả GSC hiện ở bảng */}
							{item.status === 'published' && (
								<Alert
									type="success" showIcon style={{marginBottom: 12}}
									message="Bài đã đăng lên kênh"
									description={item.external_url
										? <a href={item.external_url} target="_blank" rel="noreferrer">{item.external_url}</a>
										: 'Đăng dạng nháp — vào trang quản trị của kênh để duyệt.'}
								/>
							)}

							{/* Đợt 3: bài hoàn tất + có kênh → panel đăng bài */}
							{item.status === 'done' && activeTargets.length > 0 && (
								<div className="app-card" style={{padding: 12, marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center'}}>
									<FontAwesomeIcon icon="fa-light fa-paper-plane" />
									<Select
										placeholder="Chọn kênh đăng"
										style={{minWidth: 200}}
										value={publishTarget}
										onChange={setPublishTarget}
										options={activeTargets.map((t) => ({value: t.id, label: `${t.name} (${t.type})`}))}
									/>
									<Select
										style={{minWidth: 190}}
										value={publishMode}
										onChange={setPublishMode}
										options={[
											{value: '', label: 'Chế độ mặc định của kênh'},
											{value: 'draft', label: 'Đăng NHÁP'},
											{value: 'publish', label: 'Đăng CÔNG KHAI'},
										]}
									/>
									<Button small primary loading={publishing} onClick={doPublish}
										leftIcon={<FontAwesomeIcon icon="fa-light fa-paper-plane" />}>
										Đăng bài
									</Button>
								</div>
							)}

							<div style={{display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#6b7280', marginBottom: 12}}>
								<span><b>Meta title:</b> {item.meta_title || '—'}</span>
								<span><b>Meta description:</b> {item.meta_desc || '—'}</span>
								<span><b>Token đã dùng:</b> {nf(item.tokens)}</span>
								{item.intent_source === 'serp' && <Tag color="blue">Intent từ SERP thật{item.intent?.checked_date ? ` (${item.intent.checked_date})` : ''}</Tag>}
							</div>

							{featured && (
								<figure style={{margin: '0 0 12px'}}>
									<img src={featured.url} alt={featured.alt} style={{maxWidth: '100%', borderRadius: 8}} />
									<figcaption style={{fontSize: 12, color: '#9ca3af'}}>Ảnh đại diện{featured.credit ? ` · ${featured.credit}` : ''}</figcaption>
								</figure>
							)}

							{/* Đợt 2: thay/upload ảnh từng slot (featured + 2 body) — khoá sau khi đã đăng */}
							{['review', 'done'].includes(item.status) && (
							<div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12}}>
								<Upload accept="image/*" showUploadList={false} beforeUpload={(f) => uploadImage(f, 'featured', 0)}>
									<Button small loading={uploading} leftIcon={<FontAwesomeIcon icon="fa-light fa-image" />}>
										{featured ? 'Thay ảnh đại diện' : 'Thêm ảnh đại diện'}
									</Button>
								</Upload>
								{[0, 1].map((i) => {
									const body = (item.images || []).filter((im) => im.role === 'body');
									return (
										<Upload key={i} accept="image/*" showUploadList={false} beforeUpload={(f) => uploadImage(f, 'body', i)}>
											<Button small loading={uploading} leftIcon={<FontAwesomeIcon icon="fa-light fa-image" />}>
												{body[i] ? `Thay ảnh thân bài ${i + 1}` : `Thêm ảnh thân bài ${i + 1}`}
											</Button>
										</Upload>
									);
								})}
							</div>
							)}

							<Collapse
								style={{marginBottom: 12}}
								items={[{
									key: 'checklist',
									label: `Checklist SEO — ${item.checklist_score}/100 điểm${item.revise_rounds > 0 ? ` (AI đã sửa ${item.revise_rounds} vòng)` : ''}`,
									children: <ChecklistView checklist={item.checklist} />,
								}]}
							/>

							<div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap'}}>
								{!editing && ['review', 'done'].includes(item.status) && (
									<Button small onClick={() => { setDraft(item.content_md || ''); setEditing(true); }}
										leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}>
										Sửa nội dung
									</Button>
								)}
								<Button small onClick={() => { navigator.clipboard?.writeText(item.content_md || ''); message.success('Đã copy markdown'); }}
									leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}>
									Copy markdown
								</Button>
							</div>

							{editing ? (
								<>
									<Input.TextArea rows={20} value={draft} onChange={(e) => setDraft(e.target.value)} style={{fontFamily: 'monospace', fontSize: 13}} />
									<div style={{display: 'flex', gap: 8, marginTop: 8}}>
										<Button small primary loading={saving} onClick={saveDraft}>
											Lưu
										</Button>
										<Button small onClick={() => setEditing(false)}>Huỷ</Button>
									</div>
								</>
							) : (
								<div className="app-card" style={{padding: 16}}>
									<Markdown content={item.content_md} />
								</div>
							)}
						</>
					)}

					{/* ---- Đang chạy nhưng đã có dàn ý → xem trước ---- */}
					{item.running && item.outline?.title && !['review', 'done'].includes(item.status) && (
						<Collapse
							items={[{key: 'outline', label: 'Dàn ý đã duyệt', children: <OutlineView item={item} />}]}
						/>
					)}
				</div>
			)}
		</Drawer>
	);
}

export default ContentViewer;
